import { useCallback, useEffect, useMemo, useState } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { convertDistance, getDistance } from "geolib"
import { Product, ProductVariant, SelectedOption } from "@shopify/hydrogen-react/storefront-api-types"

import { useConfigContext } from "@app/providers/config"
import { useLocationContext } from "@app/providers/location"
import { useCore, useStorage } from "@app/hooks/useCore"
import { useFunctions } from "@app/hooks/useFunctions"
import { useImage } from "@app/hooks/useImage"
import { useMedia } from "@app/hooks/useMedia"
import { useRoutes } from "@app/hooks/useRoutes"

export type LatLng = {
  lat: number
  lng: number
}

export type Store = GatsbyTypes.SanityStoreFragmentFragment & {
  key: string
  enableClickAndCollect: boolean
  distance: number
  link: any
  imagePrimary?: any
  imageSecondary?: any
  location: LatLng
  stock: number
}

type ProductPayload = {
  stockWithBuffer: number
}

type StorePayload = {
  products: Array<ProductPayload>
  storeId: string
}

type StockPayload = {
  body: { stores: Array<StorePayload> | null }
  status: string
}

export type useFindInStore = {
  errors: Array<string>
  handleChange: (event: React.BaseSyntheticEvent) => void
  handleGeolocate: () => Promise<void>
  handleOption: (option: SelectedOption) => void
  handleSubmit: (event: React.BaseSyntheticEvent) => void
  handleShowAll: (event: React.BaseSyntheticEvent) => void
  hasMore: boolean
  isLoading: boolean
  position: LatLng | null
  search: string
  searched: string
  selectedOptions: Array<SelectedOption>
  settings: GatsbyTypes.StaticStoresQuery["settings"]
  stores: Array<Store>
  variant: ProductVariant | null
}

export const useFindInStore = (product: Product, selectedVariant?: ProductVariant): useFindInStore => {
  const { stores: allStores, settings } = useStaticQuery<GatsbyTypes.StaticStoresQuery>(graphql`
    query StaticStores {
      stores: allSanityStore(filter: { available: { eq: true }, enableInStoreAvailability: { eq: true } }) {
        edges {
          node {
            ...SanityStoreFragment
          }
        }
      }
      settings: sanityTemplateProduct {
        lowStockThreshold
        noStockThreshold
        searchRadius
      }
    }
  `)
  const {
    store: { locationLookupUrl },
    settings: { keys },
  } = useConfigContext()
  const { store } = useLocationContext()
  const {
    helpers: { edgeNormaliser },
  } = useCore()
  const { callFunction } = useFunctions()
  const { getGatsbyImage } = useImage()
  const { isMedium } = useMedia()
  const { urlResolver } = useRoutes()
  const { setStorage, getStorage, removeStorage } = useStorage()
  const [errors, setErrors] = useState<Array<string>>([])
  const [search, setSearch] = useState<string>(getStorage(keys.place) || "")
  const [searched, setSearched] = useState<string>(search)
  const [selectedOptions, setSelectedOptions] = useState<Array<SelectedOption>>([])
  const [variant, setVariant] = useState<ProductVariant | null>(null)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [position, setPosition] = useState<LatLng | null>(null)
  const truncatedLimit = 5
  const [showAll, setShowAll] = useState(false)
  const [storesWithStock, setStoresWithStock] = useState<Array<Store>>([])
  const { sku } = variant || {}

  const handleOption = useCallback(
    option => setSelectedOptions(selectedOptions?.map(selectedOption => (selectedOption.name === option.name ? option : selectedOption))),
    [selectedOptions, setSelectedOptions]
  )

  useEffect(() => {
    if (!product?.variants?.length) return
    const defaultOptions: Array<SelectedOption> = (
      (!variant && product.variants.find(variant => variant.id === selectedVariant.id)) ||
      product.variants[0]
    ).selectedOptions

    setSelectedOptions(defaultOptions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id])

  useEffect(() => {
    if (!product?.variants?.length) return
    const matchedVariant =
      product.variants.find(
        ({ selectedOptions: variantOptions }: { selectedOptions: any }) =>
          variantOptions?.filter(
            (variantOption: any) =>
              variantOption.value === selectedOptions.find((selectedOption: any) => selectedOption.name === variantOption.name)?.value
          )?.length === selectedOptions?.length
      ) || null
    setVariant(matchedVariant)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions])

  const distance = useMemo(() => settings?.searchRadius, [settings?.searchRadius])

  const handleDistance = useCallback(
    (position: any, location: any) =>
      location && position
        ? Math.round(
            (convertDistance(
              getDistance({ latitude: position.lat, longitude: position.lng }, { latitude: location.lat, longitude: location.lng }),
              `km`
            ) +
              Number.EPSILON) *
              100
          ) / 100
        : 0,
    []
  )

  const storesWithinSearch: Array<Omit<Store, "stock">> = useMemo(
    () =>
      edgeNormaliser(allStores)
        .map((store: GatsbyTypes.SanityStoreFragmentFragment) => ({
          ...store,
          location: {
            lat: store.addressLatitude,
            lng: store.addressLongitude,
          },
        }))
        ?.filter(({ location }) => location && location?.lat !== null && location?.lng !== null)
        ?.filter(({ location }) => position && handleDistance(position, location) && handleDistance(position, location) < distance)
        ?.map(store => ({
          ...store,
          distance: handleDistance(position, store.location),
          imagePrimary: getGatsbyImage(store?.imagePrimary, { width: 500 }),
          imageSecondary: getGatsbyImage(store?.imageSecondary, { width: 500 }),
          link: urlResolver(store),
        }))
        ?.sort((a, b) => a.distance - b.distance),
    [allStores, distance, edgeNormaliser, getGatsbyImage, handleDistance, position, urlResolver]
  )

  const handleChange = useCallback(({ target: { value } }) => setSearch(value), [setSearch])

  const handlePlace = useCallback(
    async (place: any) => {
      if (place?.center?.length > 0) {
        setPosition(null)
        setStorage(keys.place, place.place_name, 7)
        const position = {
          lat: place.center[1],
          lng: place.center[0],
        }
        setSearched(place.place_name)
        setSearch(place.place_name)
        setPosition(position)
      }

      if (!sku) setLoading(false)
    },
    [keys, setLoading, setPosition, setSearch, setSearched, sku, setStorage]
  )

  const handleStock = useCallback(
    async (sku: string) => {
      if (!sku || (!storesWithinSearch.length && (!position || !searched))) return
      setLoading(true)

      const storeIds = storesWithinSearch.map(({ storeId }) => storeId)

      const {
        body: { stores: storesWithStock },
      }: StockPayload = await callFunction("in-store-inventory", {
        products: [sku],
        stores: storeIds,
      })

      const stores = storesWithinSearch.map(store => ({
        ...store,
        stock: storesWithStock?.find(({ storeId }) => storeId === store?.storeId)?.products?.[0]?.stockWithBuffer || 0,
      }))

      setStoresWithStock(stores)
      setShowAll(false)

      setTimeout(() => setLoading(false), 1000)
    },
    [callFunction, searched, position, setShowAll, storesWithinSearch]
  )

  const geocode = useCallback(
    async (query: string) => {
      if (query) {
        setLoading(true)
        const { status, body } = await callFunction("geocode", {
          query,
          country: store?.locationRegion,
        })

        const result = body?.features?.[0]

        if (status === "success" && result) {
          handlePlace(result)
          setLoading(false)
        } else {
          setErrors(["Could not determine address from search."])
          setLoading(false)
        }
      }
    },
    [handlePlace, setErrors, setLoading, store?.locationRegion, callFunction]
  )

  const handleSubmit = useCallback(
    async (event: any) => {
      event.preventDefault()

      if (!search) {
        setErrors(["Please enter a suburb or postcode"])

        return
      }

      setErrors([])
      if (search) geocode(search)
    },
    [geocode, search, setErrors]
  )

  const handleGeolocate = useCallback(async () => {
    setErrors([])
    setLoading(true)
    navigator.geolocation.getCurrentPosition(
      async (position: any) => {
        if (position?.coords?.latitude && position?.coords?.longitude) {
          geocode(`${position.coords.longitude},${position.coords.latitude}`)
        } else {
          setErrors([
            "There was an error determining your current location, check that location services are enabled on your device or alternatively use the search field to find your local area.",
          ])
          setLoading(false)
        }
      },
      async () => {
        const response = await fetch(locationLookupUrl)
        const userData = await response.json()

        if (userData?.latitude && userData?.longitude) {
          geocode(`${userData.longitude},${userData.latitude}`)
        } else {
          setErrors([
            "There was an error determining your current location, check that location services are enabled on your device or alternatively use the search field to find your local area.",
          ])
          setLoading(false)
        }
      }
    )
  }, [locationLookupUrl, geocode, setErrors, setLoading])

  const handleShowAll = useCallback(() => setShowAll(true), [setShowAll])

  const storesToReturn = storesWithStock.slice(0, isMedium || showAll ? storesWithStock.length : truncatedLimit)
  const hasMore = storesWithStock.length > truncatedLimit && !showAll && !isMedium

  useEffect(() => {
    if (position) setStorage(keys.position, position, 7)
  }, [keys, position, setStorage])

  useEffect(() => {
    if (sku) handleStock(sku)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku, storesWithinSearch.length])

  useEffect(() => {
    if (!search && getStorage(keys.place)) removeStorage(keys.place)
  }, [keys, search, getStorage, removeStorage])

  // intentionally only run at first render
  useEffect(() => {
    const saved = getStorage(keys.position)
    if (saved && !position) setPosition(saved)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    errors,
    handleChange,
    handleGeolocate,
    handleOption,
    handleSubmit,
    handleShowAll,
    hasMore,
    isLoading,
    position,
    search,
    searched,
    settings,
    selectedOptions,
    stores: storesToReturn,
    variant,
  }
}
